"use client";

import { NON_LOCALIZED_PREFIXES } from "@repo/locale";

import { getTabSessionId } from "./tabSessionId";

const TAB_SESSION_ID_HEADER = "x-tab-session-id";

export function setupFetchInterceptor(): void {
  if (typeof window === "undefined") {
    return;
  }

  // API route prefixes (from the single source of truth in @repo/locale), used to
  // decide which relative requests are API calls (vs page navigations). Excludes
  // "/_next" which is handled separately below. Computed here (not at module scope)
  // so it never reads @repo/locale exports during eager module evaluation.
  const API_PREFIXES = NON_LOCALIZED_PREFIXES.filter((prefix) => prefix !== "/_next");

  // Store the original fetch function
  const originalFetch = window.fetch;

  // Override fetch to intercept requests
  window.fetch = async function (input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    // Convert input to URL for analysis
    const url = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;

    // Skip intercepting Next.js internal RSC requests and framework requests
    // These are identified by:
    // 1. _rsc query parameter (React Server Components)
    // 2. _next/ paths (Next.js internal assets/data)
    // 3. Relative paths starting with / that aren't API calls
    const isRSCRequest = url.includes("?_rsc=") || url.includes("&_rsc=");
    const isNextInternalRequest = url.includes("/_next/");
    const isApiRequest = API_PREFIXES.some((prefix) => url.startsWith(`${prefix}/`));
    const isRelativeNonAPIRequest = url.startsWith("/") && !isApiRequest && !isRSCRequest;

    // Determine if the request is external (to a different origin) or internal
    const isExternalRequest = url.startsWith("http") && !url.includes(window.location.hostname);

    // Debug: Log all potentially relevant requests
    if (isRSCRequest || url.includes("_rsc") || (url.startsWith("/") && !isApiRequest)) {
      // console.log(`[fetchInterceptor] Intercepted request:`, url.substring(0, 150), {
      //   isRSCRequest,
      //   isNextInternalRequest,
      //   isRelativeNonAPIRequest,
      // });
    }

    // Don't intercept:
    // 1. External requests (e.g., to third-party APIs)
    // Intercept:
    // 1. Actual API calls (need session headers)
    // 2. RSC requests (router.refresh() uses these, and middleware needs tabSessionId)
    const shouldIntercept =
      !isExternalRequest && ((!isNextInternalRequest && !isRelativeNonAPIRequest) || isRSCRequest);

    const tabSessionId = getTabSessionId();

    let headers: Headers;
    if (init?.headers instanceof Headers) {
      headers = new Headers(init.headers);
    } else if (Array.isArray(init?.headers)) {
      headers = new Headers(init?.headers as HeadersInit);
    } else if (init?.headers && typeof init.headers === "object") {
      headers = new Headers(init.headers as Record<string, string>);
    } else {
      headers = new Headers();
    }

    if (shouldIntercept && tabSessionId && !headers.has(TAB_SESSION_ID_HEADER)) {
      headers.set(TAB_SESSION_ID_HEADER, tabSessionId);
    }

    const newInit: RequestInit = {
      ...init,
      headers,
    };

    return originalFetch(input, newInit as RequestInit);
  };
}

export function removeFetchInterceptor(): void {
  if (typeof window === "undefined") {
    return;
  }
  // No-op for now; leaving present for symmetry
}
